/**
 * ADR-037 WhatsApp (Infobip) API helpers.
 *
 * backend-v2 wraps /api/* 200 responses in `{ statusCode, data }`; `unwrap`
 * returns the inner payload (and tolerates an unwrapped body).
 */
import apiCore from '../../utils/api-core';

export interface WhatsAppSender {
  botmapId: string;
  botId: string;
  displayName: string;
  sender: string;
  wabaId: string | null;
  status: string;
  startModuleId: string | null;
  assignedUsers: number;
  entryPoint: string;
  createdAt?: string;
}

export interface WhatsAppSettings {
  attribution: boolean;
  format: string;
}

export interface WhatsAppSendersResponse {
  senders: WhatsAppSender[];
  settings: WhatsAppSettings;
}

export interface WhatsAppTemplateInput {
  /** 1-based placeholder number this value fills. */
  index: number;
  label: string;
  /** false when something else can fill the slot (an asked contact field, a fallback). Default true. */
  required?: boolean;
  /** 'input' | 'merge' | 'contact' — merge/contact inputs are filled from the contact when left empty. */
  source?: string;
  key?: string | null;
}

/**
 * One approved, mapped template of the caller's WhatsApp sender
 * (GET /api/v3/whatsapp/templates, ADR-037 phase 0A). Context placeholders
 * are resolved server-side; the recruiter only types the `inputs`.
 */
export interface WhatsAppTemplate {
  id: string;
  name: string;
  language: string;
  category?: string;
  /** Automatic use, null = manual only. */
  kind: 'interview_invite' | 'reengage' | 'interview_reminder' | string | null;
  kindLabel?: string;
  body: string;
  inputs: WhatsAppTemplateInput[];
  /** Server-rendered text with sample data; `{{n}}` may remain for input slots. */
  preview: string;
}

/** Closed-window template for the composer (GET /api/v3/whatsapp/conversations/{id}/window). */
export interface WhatsAppWindowTemplate {
  kind: string;
  name: string;
  preview: string | null;
  /** false = the template has no slot for the recruiter's text, so the text is held until a reply. */
  hasTextParam?: boolean;
}

export interface WhatsAppWindowInfo {
  conversationId: string;
  open: boolean;
  fallbacks?: { sms: boolean; template: boolean; hold: boolean };
  template: WhatsAppWindowTemplate | null;
}

const unwrap = <T>(res: any): T => (
  res && typeof res === 'object' && 'statusCode' in res && 'data' in res ? res.data : res
);

/** Read-only: the caller's own company sender(s). Provisioning is GoHire-admin only on /companies. */
export function getWhatsAppSenders(): Promise<WhatsAppSendersResponse> {
  return apiCore.get('/api/v3/whatsapp/senders').then((res: any) => {
    const data = unwrap<any>(res) || {};
    return {
      senders: Array.isArray(data.senders) ? data.senders : [],
      settings: data.settings || { attribution: true, format: '{user} ({company}): ' },
    };
  });
}

export function getWhatsAppTemplates(kind?: string): Promise<WhatsAppTemplate[]> {
  const qs = kind ? `?kind=${encodeURIComponent(kind)}` : '';
  return apiCore.get(`/api/v3/whatsapp/templates${qs}`).then((res: any) => {
    const data = unwrap<any>(res);
    return (Array.isArray(data) ? data : []).map((t: any) => ({
      ...t,
      inputs: Array.isArray(t.inputs) ? t.inputs : [],
      preview: t.preview || t.body || '',
    }));
  });
}

export function getWhatsAppWindow(conversationId: string): Promise<WhatsAppWindowInfo | null> {
  return apiCore.get(`/api/v3/whatsapp/conversations/${conversationId}/window`)
    .then((res: any) => unwrap<WhatsAppWindowInfo>(res) || null);
}

/** Human label for a template's automatic use. */
export const TEMPLATE_KIND_LABEL: Record<string, string> = {
  interview_invite: 'Interview invite',
  reengage: 'Continue conversation',
  interview_reminder: 'Interview reminder',
};

export function templateKindLabel(kind: string | null | undefined): string {
  if (!kind) return 'Manual only';
  return TEMPLATE_KIND_LABEL[kind] || kind;
}

/**
 * Server preview of one of the caller's templates for the New Message dialog:
 * `inputs` answer `template.inputs` in order; `contactId` renders a known contact.
 */
export function previewWhatsAppTemplate(
  templateId: string,
  body: { contactId?: string | null; inputs?: string[] },
): Promise<string> {
  return apiCore.post(`/api/v3/whatsapp/templates/${templateId}/preview`, {
    ...(body.contactId ? { contactId: body.contactId } : {}),
    inputs: body.inputs || [],
  }).then((res: any) => {
    const data = unwrap<any>(res) || {};
    return String(data.text || '');
  });
}

/** Per-slot values recovered by aligning a rendered text with its `{{n}}` body; null when they don't line up. */
export function slotValuesFromRendered(body: string, rendered: string): Record<number, string> | null {
  const order: number[] = [];
  const pattern = String(body || '').split(/(\{\{\s*\d+\s*\}\})/).map((part) => {
    const m = part.match(/^\{\{\s*(\d+)\s*\}\}$/);
    if (m) {
      order.push(Number(m[1]));
      return '([\\s\\S]*?)';
    }
    return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }).join('');
  if (!order.length) return null;
  const match = new RegExp(`^${pattern}$`).exec(String(rendered || ''));
  if (!match) return null;
  const out: Record<number, string> = {};
  order.forEach((idx, i) => { if (out[idx] === undefined) out[idx] = match[i + 1]; });
  return out;
}

/**
 * Preview for the New Message dialog: the server preview with the typed
 * input values in their slots. A required input still empty shows an
 * ellipsis; an optional one keeps the server's value (the contact field).
 */
export function renderTemplatePreview(
  t: Pick<WhatsAppTemplate, 'preview' | 'body' | 'inputs'>,
  values: Record<number, string>,
): string {
  const base = t.preview || t.body || '';
  const inputs = t.inputs || [];
  const byIdx = new Map(inputs.map((i) => [Number(i.index), i]));
  const typed = (idx: number): string => (values[idx] && String(values[idx]).trim() ? String(values[idx]) : '');
  const slotRe = /\{\{\s*(\d+)\s*\}\}/g;

  // The server left the input slots open: fill them in place.
  if (inputs.some((i) => new RegExp(`\\{\\{\\s*${Number(i.index)}\\s*\\}\\}`).test(base))) {
    return base.replace(slotRe, (m, n) => {
      const idx = Number(n);
      if (!byIdx.has(idx)) return m;
      return typed(idx) || '…';
    });
  }
  // The server rendered every slot: re-render the body with its values and the typed ones.
  const recovered = t.preview && t.body ? slotValuesFromRendered(t.body, t.preview) : null;
  if (recovered && inputs.length) {
    return String(t.body).replace(slotRe, (m, n) => {
      const idx = Number(n);
      const input = byIdx.get(idx);
      if (!input) return recovered[idx] !== undefined ? recovered[idx] : m;
      return typed(idx) || (input.required === false ? recovered[idx] || '…' : '…');
    });
  }
  return base;
}

/** Message the API attached to a rejected request, or a fallback. */
export function apiErrorMessage(err: any, fallback: string): string {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  return err.message || (err.response && err.response.data && err.response.data.message) || fallback;
}
